/**
 * GOOGLE REVIEWS — SINGLE SOURCE OF TRUTH (display layer)
 *
 * De rating + review-aantal staan op ÉÉN plek: `lib/google-reviews.data.json`.
 * Dit bestand levert die getallen locale-correct geformatteerd aan de UI.
 *
 * UPDATEN:
 *  - Automatisch (live uit Google Business): `npm run sync:reviews`
 *  - Handmatig: pas het getal aan in `google-reviews.data.json` en run `npm run sync:reviews`
 *    (dat propageert ook naar de statische AI-bestanden in /public).
 *
 * PERFORMANCE: puur build/server-side (geen client fetch). `Intl.NumberFormat`
 * is verwaarloosbaar en zorgt dat het duizendtal-scheidingsteken per taal klopt
 * (NL/DE "4.852", EN "4,852", SV "4 852") en de komma in de rating ("4,7" vs "4.7").
 */

import { type Locale } from './i18n';
import data from './google-reviews.data.json';

export const GOOGLE_REVIEWS_DATA = {
  rating: data.rating,
  reviewCount: data.reviewCount,
  lastSynced: data.lastSynced,
  source: data.source,
} as const;

const INTL_LOCALE: Record<Locale, string> = {
  en: 'en-US',
  nl: 'nl-NL',
  de: 'de-DE',
  sv: 'sv-SE',
};

/** Review-aantal geformatteerd volgens de taal (bijv. "4.852" / "4,852" / "4 852"). */
export function formatReviewCount(locale: Locale): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale] ?? 'en-US').format(data.reviewCount);
}

/** Rating geformatteerd volgens de taal (bijv. "4,7" NL/DE/SV, "4.7" EN). */
export function formatRating(locale: Locale): string {
  return new Intl.NumberFormat(INTL_LOCALE[locale] ?? 'en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(data.rating);
}

export interface GoogleReviewMessage {
  /** Verbindend woord tussen rating en aantal, bijv. "rating from" / "uit" / "aus" / "betyg från". */
  text?: string;
  /** Template met `{count}` placeholder, bijv. "{count} reviews" / "{count} Bewertungen". */
  reviews?: string;
}

export interface ResolvedGoogleReview {
  rating: string;
  text: string;
  reviews: string;
}

/**
 * Vult de vertaalde badge-teksten met de centrale (locale-geformatteerde) getallen.
 * De UI-componenten renderen exact dezelfde markup als voorheen — alleen de bron
 * van de cijfers is nu centraal.
 */
export function resolveGoogleReview(
  locale: Locale,
  msg?: GoogleReviewMessage,
): ResolvedGoogleReview {
  const count = formatReviewCount(locale);
  const template = msg?.reviews ?? '{count} reviews';
  const reviews = template.includes('{count}')
    ? template.replace('{count}', count)
    : `${count} ${template}`.trim();

  return {
    rating: formatRating(locale),
    text: msg?.text ?? '',
    reviews,
  };
}
